import { ColorVariant } from '../../components'
import { getYoYData } from '../../components/financials-table'
import { shortFormatNumber } from '../../util'
import { getIsEarningEstimate, getIsSemiAnnualEarning } from '../earnings/utils'
import { EarningsWidgetCategory } from './constants'
import {
  Earning,
  EarningValueProperty,
  EarningsCommonData,
  EarningsDataEnum,
  EarningsDataType,
  EarningsTimeframeEnum,
} from './types'

export function tickFormatter(value: any) {
  return shortFormatNumber(value, { fractions: Math.abs(value) >= 1_000 ? 0 : 2 })
}

function getEarningsDataTemplate(
  earningsWidgetCategory: EarningsWidgetCategory
): Array<Pick<EarningsDataType, 'id' | 'label' | 'labelColumnClassName' | 'color' | 'isSelected' | 'isEstimate'>> {
  if (earningsWidgetCategory === EarningsWidgetCategory.Revenue) {
    return [
      {
        label: 'Estimate',
        id: EarningValueProperty.SalesEstimate,
        color: ColorVariant.gray,
        isSelected: true,
        isEstimate: true,
      },
      { label: 'Reported', id: EarningValueProperty.SalesActual, color: ColorVariant.violet, isSelected: true },
      { label: 'Surprise', id: EarningValueProperty.Surprise, color: ColorVariant.darkGray, isSelected: false },
    ]
  }

  const isGaap = earningsWidgetCategory === EarningsWidgetCategory.EpsGaap
  return [
    {
      label: 'Estimate',
      id: isGaap ? EarningValueProperty.EpsReportedEstimate : EarningValueProperty.EpsEstimate,
      color: ColorVariant.gray,
      isSelected: true,
      isEstimate: true,
    },
    {
      label: 'Reported',
      id: isGaap ? EarningValueProperty.EpsReportedActual : EarningValueProperty.EpsActual,
      color: isGaap ? ColorVariant.orange : ColorVariant.blue,
      isSelected: true,
    },
    { label: 'Surprise', id: EarningValueProperty.Surprise, color: ColorVariant.darkGray, isSelected: false },
  ]
}

export function getEffectiveTimeframe(
  quarterlyData: Earning[],
  annualData: Earning[],
  timeframe: EarningsTimeframeEnum
) {
  if (quarterlyData.length > 0 && annualData.length > 0) {
    return timeframe
  }
  if (quarterlyData.length > 0) {
    return EarningsTimeframeEnum.quarterly
  }
  return EarningsTimeframeEnum.annual
}

export function mapData(
  data: EarningsCommonData,
  timeframe: EarningsTimeframeEnum,
  earningsWidgetCategory: EarningsWidgetCategory
) {
  const isQuarterlyTimeframe =
    getEffectiveTimeframe(data.earningsData, data.earningsAnnualData, timeframe) === EarningsTimeframeEnum.quarterly // EarningsTimeframeEnum.quarterly is used for both quarterly & semiannual
  const dataValues = isQuarterlyTimeframe ? data.earningsData : data.earningsAnnualData
  const isSemiAnnualEarning = isQuarterlyTimeframe && getIsSemiAnnualEarning(data.earningsData)
  const earningsArray = getEarningsDataTemplate(earningsWidgetCategory).map((category) => {
    let numOfEstimates = 0
    return {
      ...category,
      labelColumnClassName: 'align-top',
      values: dataValues
        .map((periodDataRaw) => {
          const periodData = {
            ...periodDataRaw,
            [EarningValueProperty.SalesActual]: periodDataRaw.salesActual && periodDataRaw.salesActual * 1_000_000,
            [EarningValueProperty.SalesEstimate]:
              periodDataRaw.salesEstimate && periodDataRaw.salesEstimate * 1_000_000,
          }
          const yearPeriod = isQuarterlyTimeframe
            ? `${periodData.fiscalPeriod.substring(4, 6)} '${periodData.fiscalPeriod.substring(2, 4)}` // eg. Q2 '23
            : periodData.fiscalPeriod.substring(0, 4) // eg. 2024
          let value = periodData[category.id]
          // percentageValue is used in Surprise row
          let percentageValue: number | null = null
          const {
            [EarningValueProperty.SalesActual]: salesActual,
            [EarningValueProperty.EpsReportedActual]: epsReportedActual,
            [EarningValueProperty.EpsActual]: epsActual,
          } = periodData

          if (category.id === EarningValueProperty.Surprise) {
            let actualValue: number | null = null
            let estimateValue: number | null = null

            if (earningsWidgetCategory === EarningsWidgetCategory.Revenue) {
              actualValue = salesActual
              estimateValue = periodData[EarningValueProperty.SalesEstimate]
            } else if (earningsWidgetCategory === EarningsWidgetCategory.EpsGaap) {
              actualValue = epsReportedActual
              estimateValue = periodData[EarningValueProperty.EpsReportedEstimate]
            } else if (earningsWidgetCategory === EarningsWidgetCategory.Eps) {
              actualValue = epsActual
              estimateValue = periodData[EarningValueProperty.EpsEstimate]
            }

            if (actualValue !== null && estimateValue !== null) {
              value = actualValue - estimateValue
              percentageValue = Math.abs(value / estimateValue) * 100
              if (value < 0) {
                percentageValue = -percentageValue
              }
            }
          }

          let analysts: number | null = null
          if (earningsWidgetCategory === EarningsWidgetCategory.Revenue) {
            analysts = periodData[EarningValueProperty.SalesAnalysts]
          } else if (earningsWidgetCategory === EarningsWidgetCategory.EpsGaap) {
            analysts = periodData[EarningValueProperty.EpsReportedAnalysts]
          } else if (earningsWidgetCategory === EarningsWidgetCategory.Eps) {
            analysts = periodData[EarningValueProperty.EpsAnalysts]
          }

          return {
            year: yearPeriod,
            percentageValue,
            value,
            isEstimate: getIsEarningEstimate(periodData),
            analysts,
          }
        })
        .filter(({ isEstimate }) => {
          if (isEstimate) {
            numOfEstimates += 1
            return numOfEstimates <= 4
          }
          return true
        })
        .slice(-12),
    }
  })

  return earningsArray.map((earningsCategory) => {
    const yoyData =
      earningsCategory.id === EarningValueProperty.Surprise
        ? null
        : getYoYData(earningsCategory.values, isQuarterlyTimeframe)

    let yoyGrowthLabel = 'YoY Growth'

    if (isQuarterlyTimeframe) {
      yoyGrowthLabel = isSemiAnnualEarning ? 'SoS Growth' : 'QoQ Growth'
    }

    return {
      ...earningsCategory,
      subData: [
        ...(earningsCategory.isEstimate
          ? [
              {
                id: EarningsDataEnum.analysts,
                label: '# of Analysts',
                values: earningsCategory.values.map((anualDividend) => ({
                  ...anualDividend,
                  value: anualDividend.analysts,
                })),
              },
            ]
          : []),
        ...(yoyData && isQuarterlyTimeframe
          ? [
              {
                id: EarningsDataEnum.extraChangeYoYonQoQ,
                label: 'YoY Growth',
                values: earningsCategory.values.map((anualDividend) => ({
                  ...anualDividend,
                  value: yoyData.yoyOnQoqChange[anualDividend.year] ?? null,
                })),
              },
              {
                id: EarningsDataEnum.extraChangePctYoYonQoQ,
                label: 'YoY Growth %',
                values: earningsCategory.values.map((anualDividend) => ({
                  ...anualDividend,
                  value: yoyData.yoyOnQoqChangePct[anualDividend.year] ?? null,
                })),
              },
            ]
          : []),
        ...(yoyData
          ? [
              {
                id: EarningsDataEnum.mainChange,
                label: yoyGrowthLabel,
                values: earningsCategory.values.map((anualDividend) => ({
                  ...anualDividend,
                  value: yoyData.change[anualDividend.year] ?? null,
                })),
              },
              {
                id: EarningsDataEnum.mainChangePct,
                label: `${yoyGrowthLabel} %`,
                values: earningsCategory.values.map((anualDividend) => ({
                  ...anualDividend,
                  value: yoyData.changePct[anualDividend.year] ?? null,
                })),
              },
            ]
          : []),
      ],
    }
  })
}

function dateOrNextMonth(date: Date, day: number): Date {
  if (date.getDate() === day) {
    return new Date(date.getFullYear(), date.getMonth(), day)
  }
  return new Date(date.getFullYear(), date.getMonth() + 1, day)
}

export function getMonthlyTicks(startingTimestamp: number, endingTimestamp: number, midMonth?: boolean) {
  const MID_MONTH = 15
  const ticks: number[] = []
  const endingDate = new Date(endingTimestamp)
  let date = dateOrNextMonth(new Date(startingTimestamp), midMonth ? MID_MONTH : 1)
  while (date <= endingDate) {
    ticks.push(date.getTime())
    date = new Date(date.getFullYear(), date.getMonth() + 1, midMonth ? MID_MONTH : 1)
  }
  return ticks
}
